import { FieldValues, useController, Control } from 'react-hook-form'
import { Button } from 'devextreme-react'

/**
 * Props for the FileExtensionPicker component.
 *
 * @typedef {Object} FileExtensionPickerProps
 * @property {string} name - The name of the form field this component is bound to (should be an array of selected extensions).
 * @property {Control<FieldValues>} control - The React Hook Form control object from useForm().
 * @property {string[]} extensionsList - The list of available file extensions to select from.
 */
interface FileExtensionPickerProps {
  name: string
  control: Control<FieldValues>
  extensionsList: string[]
}

/**
 * FileExtensionPicker is a reusable form component for selecting one or more file extensions.
 *
 * It integrates with React Hook Form using `useController`, and renders a list of buttons
 * representing selectable extensions. Selected extensions are visually highlighted and stored
 * in the form state as an array of strings.
 *
 * ### Example Usage:
 * ```tsx
 * <FileExtensionPicker
 *   control={control}
 *   name="fileExtensions"
 *   extensionsList={['.csv', '.pdf', '.xml']}
 * />
 * ```
 *
 * @component
 * @param {FileExtensionPickerProps} props
 */

const ICSFileExtensionPicker = ({ name, control, extensionsList }) => {
  const {
    field: { value, onChange }
  } = useController<FieldValues>({
    name,
    control
  })

  const toggleItem = (item) => {
    onChange(
      value.includes(item) ? value.filter((i) => i !== item) : [...value, item]
    )
  }

  return (
    <div className="dg-file-ext-container">
      <div className="dg-file-ext-list search-file-ext-list">
        {extensionsList.map((i) => (
          <Button
            key={`ExtPicker${name}${i}`}
            className={`dg-ext-btn ${value.includes(i) ? 'active' : ''}`}
            onClick={() => toggleItem(i)}
          >
            <div className="dg-ext-btn-text">{i}</div>
          </Button>
        ))}
      </div>
    </div>
  )
}

export default ICSFileExtensionPicker
